import { useEffect, useState } from "react";
import api, { fmtMoney } from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, TrendingUp, TrendingDown, Wallet, BookOpen, FileCheck2, FileClock, Scale, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const TONES = {
  slate: { icon: "text-slate-500", text: "text-slate-700" },
  emerald: { icon: "text-emerald-500", text: "text-emerald-700" },
  amber: { icon: "text-amber-500", text: "text-amber-700" },
  rose: { icon: "text-rose-500", text: "text-rose-700" },
  brand: { icon: "text-brand-600", text: "text-brand-700" },
};

const Card = ({ icon: Icon, label, value, tone = "slate", testid }) => {
  const t = TONES[tone] || TONES.slate;
  return (
    <div className="rounded-xl border bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400"><Icon className={`h-4 w-4 ${t.icon}`} /><span className="text-[11px] uppercase font-mono tracking-wide">{label}</span></div>
      <p className={`mt-1.5 font-mono font-bold text-lg ${t.text}`}>{value}</p>
    </div>
  );
};

export default function AccountingDashboard() {
  const [data, setData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [pid, setPid] = useState("");
  const [trend, setTrend] = useState(null);
  const [seuil, setSeuil] = useState(0);

  const load = (p) => {
    const params = p ? { period_id: p } : {};
    api.get("/accounting/dashboard", { params }).then((r) => setData(r.data));
    api.get("/accounting/dashboard-trend", { params }).then((r) => setTrend(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/accounting/periods").then((r) => { setPeriods(r.data); const a = r.data.find((x) => x.active) || r.data[0]; setPid(a?.id || ""); }).catch(() => {});
    api.get("/accounting/settings").then((r) => setSeuil(r.data.tresorerie_seuil || 0)).catch(() => {});
    load();
  }, []);
  const saveSeuil = () => api.put("/accounting/settings", { tresorerie_seuil: Number(seuil) || 0 }).then(() => { toast.success("Seuil d'alerte enregistré"); load(pid); }).catch(() => toast.error("Erreur"));

  const onPeriod = (v) => { setPid(v); setData(null); load(v); };
  if (!data) return <Loader />;

  const resultTone = data.resultat >= 0 ? "emerald" : "rose";
  return (
    <div>
      <PageHeader title="Comptabilité" subtitle="Tableau de bord comptable — SYSCOHADA révisé (FCFA)">
        <Select value={pid} onValueChange={onPeriod}>
          <SelectTrigger className="w-56" data-testid="acc-period-select"><SelectValue placeholder="Exercice" /></SelectTrigger>
          <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.libelle}{p.active ? " (actif)" : ""}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-1"><Input type="number" value={seuil} onChange={(e) => setSeuil(e.target.value)} placeholder="Seuil trésorerie" className="w-40" data-testid="tresorerie-seuil-input" /><Button variant="outline" size="sm" onClick={saveSeuil} data-testid="tresorerie-seuil-save">OK</Button></div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {data.tresorerie_alerte && (
          <div className="col-span-2 lg:col-span-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex items-center gap-2" data-testid="tresorerie-alerte">
            <AlertTriangle className="h-4 w-4 shrink-0" />Trésorerie ({fmtMoney(data.tresorerie)}) sous le seuil d'alerte ({fmtMoney(data.tresorerie_seuil)}).
          </div>
        )}
        <Card icon={TrendingUp} label="Produits" value={fmtMoney(data.total_produits)} tone="emerald" testid="acc-kpi-produits" />
        <Card icon={TrendingDown} label="Charges" value={fmtMoney(data.total_charges)} tone="amber" testid="acc-kpi-charges" />
        <Card icon={Scale} label="Résultat" value={fmtMoney(data.resultat)} tone={resultTone} testid="acc-kpi-resultat" />
        <Card icon={Wallet} label="Trésorerie" value={fmtMoney(data.tresorerie)} tone="brand" testid="acc-kpi-tresorerie" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={BookOpen} label="Écritures" value={data.nb_ecritures} testid="acc-kpi-ecritures" />
        <Card icon={FileCheck2} label="Validées" value={data.nb_validees} tone="emerald" testid="acc-kpi-validees" />
        <Card icon={FileClock} label="Brouillons" value={data.nb_brouillons} tone="amber" testid="acc-kpi-brouillons" />
        <Card icon={Calculator} label="Comptes · Journaux" value={`${data.nb_comptes} · ${data.nb_journaux}`} testid="acc-kpi-comptes" />
      </div>

      {trend && trend.months && trend.months.length > 0 && (
        <div className="mt-6 rounded-xl border bg-white p-4" data-testid="acc-trend-chart">
          <h3 className="font-head font-semibold mb-3">Évolution mensuelle — résultat &amp; trésorerie</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={trend.months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(v)} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="resultat" name="Résultat du mois" fill="#076633" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="tresorerie" name="Trésorerie cumulée" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <p className="mt-6 text-sm text-slate-400">Le résultat, les produits/charges et la trésorerie sont calculés à partir des <span className="font-medium text-slate-600">écritures validées</span> de l'exercice sélectionné.</p>
    </div>
  );
}
